import { devHostnames, localhostUrlBuilder, prodHostnames, qubictronAppsUrlMap, qubictronUrlMap } from "../shared/url.utils";
import { UrlBuilder } from "../shared/urlBuilder";
class ClientUrlBuilder extends UrlBuilder {
  constructor(url = window.location.href) {
    super(url);
    this.incomingUrl = new UrlBuilder(url);
  }
  clone() {
    return new ClientUrlBuilder(this.build());
  }
}

/**
 * A callback function that resolves and returns a URL string
 * Used to determine the appropriate URL based on environment (dev, prod, etc)
 * @returns {Object} An object containing the resolved URL string
 * @returns {string} url - The resolved URL string
 */

/**
 * Helper function that sets a new URL from a callback if provided, otherwise executes fallback
 * @param {Object} options - The options object
 * @param {UrlBuilder} options.builder - The URL builder instance to modify
 * @param {ResolveCallback | undefined} options.callback - The resolution callback function to execute if present
 * @param {() => void} options.onCallbackAbsent - Fallback function to execute if callback is not provided
 */
const handleUrlResolution = ({
  builder,
  callback,
  onCallbackAbsent
}) => {
  if (!callback) {
    onCallbackAbsent();
    return;
  }
  builder.setUrl(new UrlBuilder(callback().url).origin);
};
export class QubictronUrlBuilder extends ClientUrlBuilder {
  #devHostnames = new Set(devHostnames);
  #prodHostnames = new Set(prodHostnames);
  constructor({
    url,
    originResolverCallbackMap,
    skipBaseResolution
  } = {}) {
    super(url);
    this.originResolverCallbackMap = originResolverCallbackMap;
    this.skipBaseResolution = skipBaseResolution;
    if (!this.skipBaseResolution) {
      this.setCorrectBaseUrl();
    }
  }
  clone() {
    // Preserve full URL and hostnames; keep same originResolverCallbackMap
    return new QubictronUrlBuilder({
      url: this.build(),
      originResolverCallbackMap: this.originResolverCallbackMap,
      skipBaseResolution: true
    }).setProdHostnames(Array.from(this.prodHostnames)).setDevHostnames(Array.from(this.devHostnames));
  }
  setDevHostnames(hostnames) {
    this.#devHostnames = new Set(hostnames);
    return this;
  }
  setProdHostnames(hostnames) {
    this.#prodHostnames = new Set(hostnames);
    return this;
  }
  addDevHostname(hostname) {
    this.#devHostnames.add(hostname);
    return this;
  }
  addProdHostname(hostname) {
    this.#prodHostnames.add(hostname);
    return this;
  }
  get devHostnames() {
    return this.#devHostnames;
  }
  get prodHostnames() {
    return this.#prodHostnames;
  }

  /**
   * Checks if the current host is a development host.
   * @returns {boolean} True if the host is a development host, false otherwise.
   */
  isDevHost() {
    return this.#devHostnames.has(this.incomingUrl.hostname);
  }

  /**
   * Checks if the current host is a production host.
   * @returns {boolean} True if the host is a production host, false otherwise.
   */
  isProdHost() {
    return this.#prodHostnames.has(this.incomingUrl.hostname);
  }

  /**
   * Sets the protocol and host based on the incoming URL and configuration.
   * @private
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setCorrectBaseUrl() {
    switch (this.incomingUrl.hostname) {
      case localhostUrlBuilder().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.localhost,
          onCallbackAbsent: () => {
            this.setUrl(qubictronUrlMap.dev().origin);
          }
        });
        break;
      case qubictronUrlMap.loc().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.loc,
          onCallbackAbsent: () => {
            this.setUrl(qubictronUrlMap.loc().origin);
          }
        });
        break;
      case qubictronUrlMap.dev().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.dev,
          onCallbackAbsent: () => {
            this.setUrl(qubictronUrlMap.dev().origin);
          }
        });
        break;
      case qubictronUrlMap.release().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.release,
          onCallbackAbsent: () => {
            this.setUrl(qubictronUrlMap.release().origin);
          }
        });
        break;
      case qubictronUrlMap.prod().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.prod,
          onCallbackAbsent: () => {
            this.setUrl(qubictronUrlMap.prod().origin);
          }
        });
        break;
      default:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.default,
          onCallbackAbsent: () => {
            this.setUrl(qubictronUrlMap.prod().origin);
          }
        });
    }
    return this;
  }
}
export class QubictronAppsUrlBuilder extends ClientUrlBuilder {
  #devHostnames = new Set(devHostnames);
  #prodHostnames = new Set(prodHostnames);
  constructor({
    url,
    originResolverCallbackMap,
    skipBaseResolution
  } = {}) {
    super(url);
    this.originResolverCallbackMap = originResolverCallbackMap;
    this.skipBaseResolution = skipBaseResolution;
    if (!this.skipBaseResolution) {
      this.setCorrectBaseUrl();
    }
  }
  clone() {
    return new QubictronAppsUrlBuilder({
      url: this.build(),
      originResolverCallbackMap: this.originResolverCallbackMap,
      skipBaseResolution: true
    }).setProdHostnames(Array.from(this.prodHostnames)).setDevHostnames(Array.from(this.devHostnames));
  }
  setDevHostnames(hostnames) {
    hostnames.forEach(hostname => this.#devHostnames.add(hostname));
    return this;
  }
  setProdHostnames(hostnames) {
    hostnames.forEach(hostname => this.#prodHostnames.add(hostname));
    return this;
  }
  addProdHostname(hostname) {
    this.#prodHostnames.add(hostname);
    return this;
  }
  addDevHostname(hostname) {
    this.#devHostnames.add(hostname);
    return this;
  }
  get devHostnames() {
    return this.#devHostnames;
  }
  get prodHostnames() {
    return this.#prodHostnames;
  }

  /**
   * Checks if the current host is a development host.
   * @returns {boolean} True if the host is a development host, false otherwise.
   */
  isDevHost() {
    return this.#devHostnames.has(this.incomingUrl.hostname);
  }

  /**
   * Checks if the current host is a production host.
   * @returns {boolean} True if the host is a production host, false otherwise.
   */
  isProdHost() {
    return this.#prodHostnames.has(this.incomingUrl.hostname);
  }

  /**
   * Sets the protocol and host based on the incoming URL and configuration.
   * @private
   * @returns {this} The UrlBuilder instance for chaining.
   */
  setCorrectBaseUrl() {
    switch (this.incomingUrl.hostname) {
      case localhostUrlBuilder().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.localhost,
          onCallbackAbsent: () => {
            this.setUrl(this.incomingUrl.origin);
          }
        });
        break;
      case qubictronUrlMap.loc().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.loc,
          onCallbackAbsent: () => {
            this.setUrl(qubictronAppsUrlMap.dev().origin);
          }
        });
        break;
      case qubictronAppsUrlMap.dev().hostname:
      case qubictronUrlMap.dev().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.dev,
          onCallbackAbsent: () => {
            this.setUrl(qubictronAppsUrlMap.dev().origin);
          }
        });
        break;
      case qubictronAppsUrlMap.release().hostname:
      case qubictronUrlMap.release().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.release,
          onCallbackAbsent: () => {
            this.setUrl(qubictronAppsUrlMap.release().origin);
          }
        });
        break;
      case qubictronAppsUrlMap.prod().hostname:
      case qubictronUrlMap.prod().hostname:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.prod,
          onCallbackAbsent: () => {
            this.setUrl(qubictronAppsUrlMap.prod().origin);
          }
        });
        break;
      default:
        handleUrlResolution({
          builder: this,
          callback: this.originResolverCallbackMap?.default,
          onCallbackAbsent: () => {
            this.setUrl(qubictronAppsUrlMap.prod().origin);
          }
        });
    }
    return this;
  }
}